import React, { useState } from 'react';
import '../styles/contact-us.css';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  caseType: string;
  message: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  caseType?: string;
  message?: string;
}

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    caseType: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const caseTypes = [
    'Motor Vehicle Accident',
    'Slip and Fall',
    'Medical Malpractice',
    'Product Liability',
    'Workplace Injury',
    'Wrongful Death',
    'Other Personal Injury'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.caseType) {
      newErrors.caseType = 'Please select a case type';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please describe your case';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Please provide more details (at least 20 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to FormSubmit
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', `${formData.firstName} ${formData.lastName}`);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('phone', formData.phone);
      formDataToSubmit.append('Case Type', formData.caseType);
      formDataToSubmit.append('message', formData.message);
      formDataToSubmit.append('_subject', `New Contact Form - ${formData.caseType}`);
      formDataToSubmit.append('_captcha', 'false');
      formDataToSubmit.append('_template', 'table');

      const response = await fetch('https://formsubmit.co/admin@muselegalgroup.com', {
        method: 'POST',
        body: formDataToSubmit,
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          caseType: '',
          message: ''
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setIsSubmitting(false);
      alert('There was an error submitting the form. Please try emailing us directly at admin@muselegalgroup.com or calling 818-245-8888.');
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <div className="contact-hero">
        <div className="contact-hero-content">
          <h1 className="contact-hero-headline">Let's Discuss Your Case</h1>
          <p className="contact-hero-tagline">
            Free consultation. No obligation. We're here to help you understand your rights and options.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="contact-main">
        <div className="contact-container">
          {/* Form Section */}
          <div className="contact-form-section">
            <div className="contact-form-header">
              <h2 className="contact-form-title">Get Started Today</h2>
              <p className="contact-form-description">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
            </div>

            {submitSuccess && (
              <div className="contact-success-message">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>We've received your message and will contact you soon.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form" noValidate>
              {/* Name Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`form-input ${errors.firstName ? 'error' : ''}`}
                    placeholder="John"
                  />
                  {errors.firstName && (
                    <span className="form-error">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`form-input ${errors.lastName ? 'error' : ''}`}
                    placeholder="Doe"
                  />
                  {errors.lastName && (
                    <span className="form-error">{errors.lastName}</span>
                  )}
                </div>
              </div>

              {/* Contact Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="john.doe@example.com"
                  />
                  {errors.email && (
                    <span className="form-error">{errors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && (
                    <span className="form-error">{errors.phone}</span>
                  )}
                </div>
              </div>

              {/* Case Type */}
              <div className="form-group">
                <label htmlFor="caseType" className="form-label">
                  Type of Case <span className="required">*</span>
                </label>
                <select
                  id="caseType"
                  name="caseType"
                  value={formData.caseType}
                  onChange={handleChange}
                  className={`form-select ${errors.caseType ? 'error' : ''}`}
                >
                  <option value="">Select a case type...</option>
                  {caseTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.caseType && (
                  <span className="form-error">{errors.caseType}</span>
                )}
              </div>

              {/* Message */}
              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Tell Us About Your Case <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                  placeholder="Please provide details about your case, including when the incident occurred and any relevant information..."
                  rows={6}
                />
                {errors.message && (
                  <span className="form-error">{errors.message}</span>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className={`form-submit-button ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Submit Consultation Request'}
              </button>

              <p className="form-disclaimer">
                By submitting this form, you agree to our privacy policy. We respect your privacy and will never share your information.
              </p>
              
              <div className="form-legal-disclaimer">
                <p className="legal-disclaimer-title">No Attorney–Client Relationship Formed:</p>
                <p className="legal-disclaimer-text">
                  Contacting us through this form does not create an attorney–client relationship, and information submitted may not be treated as confidential. 
                  An attorney–client relationship is formed only after we have completed a conflict check and both you and our firm sign a written engagement agreement.
                </p>
              </div>
            </form>
          </div>

          {/* Info Section */}
          <div className="contact-info-section">
            <div className="contact-info-card">
              <h3 className="contact-info-title">Contact Information</h3>
              
              <div className="contact-info-item">
                <div className="contact-info-icon">📞</div>
                <div className="contact-info-content">
                  <div className="contact-info-label">Phone</div>
                  <a href="tel:8182458888" className="contact-info-value">818-245-8888</a>
                  <div className="contact-info-note">Available 24/7 for emergencies</div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">✉️</div>
                <div className="contact-info-content">
                  <div className="contact-info-label">Email</div>
                  <a href="mailto:admin@muselegalgroup.com" className="contact-info-value">
                    admin@muselegalgroup.com
                  </a>
                  <div className="contact-info-note">We respond within 24 hours</div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">📍</div>
                <div className="contact-info-content">
                  <div className="contact-info-label">Office</div>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=15303+Ventura+Blvd+9th+Floor+Sherman+Oaks+CA+91403" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="contact-info-value"
                  >
                    15303 Ventura Blvd 9th Floor<br />
                    Sherman Oaks, CA 91403
                  </a>
                  <div className="contact-info-note">By appointment only</div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">⏰</div>
                <div className="contact-info-content">
                  <div className="contact-info-label">Business Hours</div>
                  <div className="contact-info-value">
                    Monday - Friday: 8:00 AM - 6:00 PM<br />
                    Saturday - Sunday: 10:00 AM - 4:00 PM
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-why-card">
              <h3 className="contact-why-title">Why Choose Muse Legal?</h3>
              <ul className="contact-why-list">
                <li className="contact-why-item">
                  <span className="why-icon">✓</span>
                  <span>No fees unless we win your case</span>
                </li>
                <li className="contact-why-item">
                  <span className="why-icon">✓</span>
                  <span>Free initial consultation</span>
                </li>
                <li className="contact-why-item">
                  <span className="why-icon">✓</span>
                  <span>Personalized attention to every case</span>
                </li>
                <li className="contact-why-item">
                  <span className="why-icon">✓</span>
                  <span>Proven track record of success</span>
                </li>
                <li className="contact-why-item">
                  <span className="why-icon">✓</span>
                  <span>Compassionate and responsive service</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Google Maps Section */}
      <div className="contact-map-section">
        <div className="contact-map-container">
          <div className="contact-map-header">
            <h2 className="contact-map-title">Serving all of California</h2>
            <p className="contact-map-description">
              Located in the heart of Sherman Oaks. Schedule an appointment to meet with our team.
            </p>
          </div>
          <div className="contact-map-wrapper">
            <iframe
              title="Muse Legal Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.3974887394856!2d-118.44869!3d34.15184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2987f0c8e5f4f%3A0x0!2s15303%20Ventura%20Blvd%2C%20Sherman%20Oaks%2C%20CA%2091403!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="contact-map-cta">
            <a 
              href="https://www.google.com/maps/dir/?api=1&destination=15303+Ventura+Blvd+9th+Floor+Sherman+Oaks+CA+91403"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-map-button"
            >
              Get Directions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;

